import type React from 'react';

import { useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import colors from '@packages/core/styles/colors';
import { type Retailer } from '@packages/models/api';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { AdminHubHeader } from '@web/administration/components/admin-hub/admin-hub-header';
import { docsService, qmrsService, vinSessionService } from '@web/services/singletons';
import {
    isPopupGuidanceDismissed,
    persistPopupGuidanceDismissed,
    PopupGuidanceBanner,
} from '@web/vin-sessions/components/stis-publications/popup-guidance-banner';
import { PopupGuidanceHelpPill } from '@web/vin-sessions/components/stis-publications/popup-guidance-help-pill';
import { type TroubleshootingSearchRequest, type TroubleshootingSearchResult } from '@web/vin-sessions/types';
import { fetchCdnDocumentUrlByDocId, openDocumentTab } from '@web/vin-sessions/utils/open-document-tab';
import { type FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

const logger = getLogger('service-diagnostics');

const ROWS_PER_PAGE = 10;

type CarlineModelYearEntry = {
    carline: FilterCarline;
    modelYearFrom: string;
    modelYearTo: string;
};

type FilterCarline = {
    description: string;
    id: string;
};

const formatDateForApi = (dateStr: string): string => {
    if (!dateStr) {
        return '';
    }
    const d = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

// Rendered in UTC to match the timestamps the API returns — and the UTC dates the search
// filter above sends. Without it the viewer's local offset shifts the day, so a session the
// backend recorded on the 12th displays as the 11th west of UTC.
const formatDate = (dateStr: string) => {
    if (!dateStr) {
        return '-';
    }
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
    });
};

const statusLabel = (c: number) => (c === 1 ? 'Completed' : 'In Progress');
const vinInputLabel = (v: number) => (v === 1 ? 'Auto' : 'Manual');

const useStyles = createUseStyles({
    '@keyframes fadeIn': {
        from: { opacity: 0, transform: 'translateY(8px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    page: {
        display: 'flex',
        flexDirection: 'column',
        animation: '$fadeIn 0.3s ease-out',
    },
    container: {
        padding: '24px 48px',
        '@media (max-width: 768px)': {
            padding: '16px',
        },
        '@media (min-width: 1920px)': {
            padding: '32px 64px',
        },
    },

    searchPanel: {
        border: `1px solid ${colors.grayThree}`,
        backgroundColor: colors.white,
        marginBottom: 32,
        overflow: 'hidden',
    },
    searchPanelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 14px',
        backgroundColor: colors.blueOne,
        cursor: 'pointer',
        userSelect: 'none',
        '@media (min-width: 1920px)': {
            padding: '12px 20px',
        },
    },
    searchPanelHeaderTitle: {
        color: colors.white,
        fontWeight: 600,
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        '@media (min-width: 1920px)': {
            fontSize: 16,
        },
    },
    searchPanelBody: {
        padding: '16px 40px 24px',
        '@media (max-width: 768px)': {
            padding: '12px 16px 20px',
        },
        '@media (min-width: 1920px)': {
            padding: '24px 60px 32px',
        },
    },
    instruction: {
        fontSize: 13,
        fontWeight: 600,
        color: colors.textDarkPrimary,
        marginBottom: 20,
        '@media (min-width: 1920px)': {
            fontSize: 15,
            marginBottom: 24,
        },
    },

    fieldGroup: {
        marginBottom: 18,
        '@media (min-width: 1920px)': {
            marginBottom: 22,
        },
    },
    fieldLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: colors.textDarkPrimary,
        marginBottom: 4,
        display: 'block',
        '@media (min-width: 1920px)': {
            fontSize: 15,
            marginBottom: 6,
        },
    },
    fieldLabelRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    fieldInput: {
        width: '100% !important',
        height: '36px !important',
        fontSize: '13px !important',
        borderRadius: '2px !important',
        border: `1px solid ${colors.grayFour} !important`,
        padding: '6px 10px !important',
        boxSizing: 'border-box !important',
        '@media (min-width: 1920px)': {
            height: '42px !important',
            fontSize: '15px !important',
            padding: '8px 12px !important',
        },
    },

    addButton: {
        display: 'inline-block',
        marginTop: 6,
        '@media (min-width: 1920px)': {
            marginTop: 8,
        },
    },

    chipList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 6,
    },
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        backgroundColor: colors.blueFour,
        border: `1px solid ${colors.blueThree}`,
        borderRadius: 3,
        padding: '2px 8px',
        fontSize: 12,
        color: colors.blueOne,
        fontWeight: 700,
    },
    chipRemove: {
        cursor: 'pointer',
        fontWeight: 'bold',
        marginLeft: 2,
        fontSize: 14,
        lineHeight: 1,
        color: colors.graySix,
        '&:hover': {
            color: colors.redOne,
        },
    },

    carlineTag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        backgroundColor: colors.blueFour,
        border: `1px solid ${colors.blueThree}`,
        borderRadius: 3,
        padding: '2px 8px',
        fontSize: 12,
        color: colors.blueOne,
        fontWeight: 700,
        marginRight: 6,
        marginBottom: 4,
    },

    radioGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        height: 34,
        '@media (min-width: 1920px)': {
            height: 42,
            gap: 20,
        },
    },
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 13,
        cursor: 'pointer',
        color: colors.textDarkPrimary,
        '@media (min-width: 1920px)': {
            fontSize: 15,
            gap: 6,
        },
    },

    actionBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 10,
        paddingTop: 12,
        marginTop: 4,
    },

    resultsHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 16,
    },
    resultCount: {
        fontSize: 14,
        color: colors.textDarkSecondary,
        fontWeight: 500,
    },
    tableWrap: {
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        overflow: 'auto',
        /* Thin blue scrollbar */
        scrollbarWidth: 'thin',
        scrollbarColor: `${colors.blueTwo} ${colors.grayTwo}`,
        '&::-webkit-scrollbar': {
            height: 6,
            width: 6,
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: colors.grayTwo,
            borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.blueTwo,
            borderRadius: 3,
            '&:hover': {
                backgroundColor: colors.blueOne,
            },
        },
        '&::-webkit-scrollbar-button': {
            display: 'none',
        },
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        minWidth: 1600,
        '& th': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: colors.blueOne,
            color: colors.white,
            fontSize: 12,
            fontWeight: 600,
            padding: '10px 12px',
            whiteSpace: 'nowrap',
            borderBottom: 'none',
            textAlign: 'left',
            overflow: 'hidden',
        },
        '& td': {
            fontSize: 13,
            padding: '9px 12px',
            color: colors.textDarkPrimary,
            borderBottom: `1px solid ${colors.grayThree}`,
            verticalAlign: 'middle',
        },
        '& tbody tr:nth-child(even)': {
            backgroundColor: colors.grayOne,
        },
        '& tbody tr:hover': {
            backgroundColor: colors.blueFour,
        },
    },
    cellTruncate: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    statusBadge: {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 3,
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
    },
    statusCompleted: {
        backgroundColor: colors.greenFour,
        color: colors.greenOne,
    },
    statusInProgress: {
        backgroundColor: colors.yellowFour,
        color: '#8a6d00',
    },
    viewLink: {
        color: colors.blueLink,
        cursor: 'pointer',
        fontWeight: 500,
        textDecoration: 'none',
        '&:hover': {
            color: colors.blueLinkHover,
            textDecoration: 'underline',
        },
    },

    paginationBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 16,
    },
    pageInfo: {
        fontSize: 13,
        color: colors.textDarkSecondary,
    },
    pageControls: {
        display: 'flex',
        gap: 4,
        alignItems: 'center',
    },
    pageBtn: {
        padding: '4px 10px',
        fontSize: 13,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 3,
        backgroundColor: colors.white,
        cursor: 'pointer',
        color: colors.textDarkPrimary,
        '&:hover:not(:disabled)': {
            backgroundColor: colors.blueFour,
            borderColor: colors.blueTwo,
        },
    },
    pageBtnActive: {
        padding: '4px 10px',
        fontSize: 13,
        border: `1px solid ${colors.blueOne}`,
        borderRadius: 3,
        backgroundColor: colors.blueOne,
        color: colors.white,
        cursor: 'pointer',
    },
    pageBtnDisabled: {
        padding: '4px 10px',
        fontSize: 13,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 3,
        backgroundColor: colors.grayOne,
        color: colors.grayFive,
        cursor: 'default',
    },

    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        color: colors.textDarkSecondary,
        gap: 10,
    },

    resultsSection: {
        transition: 'opacity 0.35s ease, transform 0.35s ease',
    },
    resultsSectionVisible: {
        opacity: 1,
        transform: 'translateY(0)',
    },
    resultsSectionHidden: {
        opacity: 0,
        transform: 'translateY(8px)',
        pointerEvents: 'none',
        maxHeight: 0,
        overflow: 'hidden',
    },
    resultsSectionHighlight: {
        outline: `3px solid ${colors.blueOne}`,
        borderRadius: 2,
        transition: 'outline 0.3s ease',
    },

    typeaheadWrap: {
        '& .rbt-input': {
            height: '36px !important',
            fontSize: '13px !important',
            borderRadius: '2px !important',
            border: `1px solid ${colors.grayFour} !important`,
            padding: '6px 28px 6px 10px !important',
            boxSizing: 'border-box !important',
            '@media (min-width: 1920px)': {
                height: '42px !important',
                fontSize: '15px !important',
                padding: '8px 28px 8px 12px !important',
            },
        },
    },
    clearIcon: {
        backgroundColor: colors.white,
        cursor: 'pointer',
        position: 'absolute',
        right: 8,
        top: 5,
        zIndex: 2,
    },
});

export const TroubleshootingSearch: FC = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { token, account } = useAuthState();
    const canSearchAll = account?.docsCapabilities?.canSearchAllTroubleShootingSessions === true;

    const [carlineOptions, setCarlineOptions] = useState<FilterCarline[]>([]);
    const [modelYearOptions, setModelYearOptions] = useState<string[]>([]);
    const [technicianOptions, setTechnicianOptions] = useState<
        { accountId: string; soaUsername: string; firstName: string; lastName: string }[]
    >([]);
    const [retailerOptions, setRetailerOptions] = useState<Retailer[]>([]);

    const [vins, setVins] = useState<string[]>([]);
    const [vinInput, setVinInput] = useState('');
    const [dtcs, setDtcs] = useState<string[]>([]);
    const [dtcInput, setDtcInput] = useState('');
    const [carlineEntries, setCarlineEntries] = useState<CarlineModelYearEntry[]>([]);
    const [pendingCarline, setPendingCarline] = useState<FilterCarline[]>([]);
    const [pendingMYFrom, setPendingMYFrom] = useState<string[]>([]);
    const [pendingMYTo, setPendingMYTo] = useState<string[]>([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [technician, setTechnician] = useState<
        { accountId: string; soaUsername: string; firstName: string; lastName: string }[]
    >([]);
    const [saveId, setSaveId] = useState('');
    const [retailerId, setRetailerId] = useState<Retailer[]>([]);
    const [status, setStatus] = useState('');
    const [vinGetStatus, setVinGetStatus] = useState('');
    const [repairOrderNumber, setRepairOrderNumber] = useState('');
    const [odometerStart, setOdometerStart] = useState('');
    const [odometerEnd, setOdometerEnd] = useState('');
    const [odometerUnit, setOdometerUnit] = useState('');

    const [searchOpen, setSearchOpen] = useState(true);
    const [results, setResults] = useState<TroubleshootingSearchResult[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [guidanceDismissed, setGuidanceDismissed] = useState<boolean>(isPopupGuidanceDismissed);
    const [guidanceReopened, setGuidanceReopened] = useState(false);
    const [popupBlocked, setPopupBlocked] = useState(false);
    const showGuidance = popupBlocked || guidanceReopened || !guidanceDismissed;

    const handleGuidanceClose = useCallback(() => {
        persistPopupGuidanceDismissed();
        setGuidanceDismissed(true);
        setGuidanceReopened(false);
        setPopupBlocked(false);
    }, []);

    const handleSearchRef = useRef<(page?: number) => void>();
    const carlineRef = useRef<any>(null);
    const myFromRef = useRef<any>(null);
    const myToRef = useRef<any>(null);
    const technicianRef = useRef<any>(null);
    const retailerRef = useRef<any>(null);

    const hasCriteria = useMemo(() => {
        return (
            vins.length > 0 ||
            dtcs.length > 0 ||
            carlineEntries.length > 0 ||
            startDate !== '' ||
            endDate !== '' ||
            technician.length > 0 ||
            saveId.trim() !== '' ||
            retailerId.length > 0 ||
            status !== '' ||
            vinGetStatus !== '' ||
            repairOrderNumber.trim() !== '' ||
            odometerStart !== '' ||
            odometerEnd !== '' ||
            odometerUnit !== ''
        );
    }, [
        vins,
        dtcs,
        carlineEntries,
        startDate,
        endDate,
        technician,
        saveId,
        retailerId,
        status,
        vinGetStatus,
        repairOrderNumber,
        odometerStart,
        odometerEnd,
        odometerUnit,
    ]);

    const breadcrumbs = useMemo(
        () => [
            {
                title: t('stisPublications:breadcrumbs.serviceDiagnostics', 'Service Diagnostics'),
                to: '/service-diagnostics',
            },
            { title: 'Search Troubleshooting Sessions' },
        ],
        [t]
    );

    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const response = await vinSessionService.getFilter({ ignoreCache: true });
                if (response.success) {
                    if (response.data.carline) {
                        setCarlineOptions(response.data.carline.filter((c: any) => c && c.description));
                    }
                    if (response.data.modelYear) {
                        setModelYearOptions(
                            response.data.modelYear.map(String).sort((a: string, b: string) => Number(b) - Number(a))
                        );
                    }
                }
            } catch (err) {
                logger.error('Failed to fetch filter options', { error: err });
            }
        };
        fetchFilters();
    }, []);

    const technicianSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const retailerSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const fetchInitialTechnicians = async () => {
            try {
                const response = canSearchAll
                    ? await docsService.searchTroubleshootingUsers('', 0)
                    : await docsService.getServiceEngineers();
                if (response.success && response.data?.allowedAccounts) {
                    setTechnicianOptions(
                        response.data.allowedAccounts.filter(
                            (t: any) => t && (t.firstName || t.lastName || t.soaUsername)
                        )
                    );
                }
            } catch (err) {
                logger.error('Failed to fetch technicians', { error: err });
            }
        };
        fetchInitialTechnicians();
    }, [canSearchAll]);

    const handleTechnicianSearch = useCallback(
        (query: string) => {
            if (!canSearchAll) {
                return;
            }
            if (technicianSearchTimer.current) {
                clearTimeout(technicianSearchTimer.current);
            }
            if (!query.trim()) {
                technicianSearchTimer.current = setTimeout(async () => {
                    try {
                        const response = await docsService.searchTroubleshootingUsers('', 0);
                        if (response.success && response.data?.allowedAccounts) {
                            setTechnicianOptions(
                                response.data.allowedAccounts.filter(
                                    (t: any) => t && (t.firstName || t.lastName || t.soaUsername)
                                )
                            );
                        }
                    } catch (err) {
                        logger.error('Failed to fetch technicians', { error: err });
                    }
                }, 300);
                return;
            }
            technicianSearchTimer.current = setTimeout(async () => {
                try {
                    const response = await docsService.searchTroubleshootingUsers(query);
                    if (response.success && response.data?.allowedAccounts) {
                        setTechnicianOptions(
                            response.data.allowedAccounts.filter(
                                (t: any) => t && (t.firstName || t.lastName || t.soaUsername)
                            )
                        );
                    } else {
                        setTechnicianOptions([]);
                    }
                } catch (err) {
                    logger.error(`Failed to fetch technicians for the query [${query}]`, { error: err, query });
                    setTechnicianOptions([]);
                }
            }, 300);
        },
        [canSearchAll]
    );

    useEffect(() => {
        const fetchRetailers = async () => {
            try {
                const response = await qmrsService.fetchRetailers({
                    searchParams: { canCreateQmr: true },
                    ignoreCache: true,
                });
                if (response.success && response.data?.retailers) {
                    setRetailerOptions(response.data.retailers.filter((r: any) => r && (r.name || r.code)));
                }
            } catch (err) {
                logger.error('Failed to fetch retailers', { error: err });
            }
        };
        fetchRetailers();
    }, []);

    const handleRetailerSearch = useCallback((query: string) => {
        if (retailerSearchTimer.current) {
            clearTimeout(retailerSearchTimer.current);
        }
        if (!query.trim()) {
            retailerSearchTimer.current = setTimeout(async () => {
                try {
                    const response = await qmrsService.fetchRetailers({
                        searchParams: { canCreateQmr: true },
                        ignoreCache: true,
                    });
                    if (response.success && response.data?.retailers) {
                        setRetailerOptions(response.data.retailers.filter((r: any) => r && (r.name || r.code)));
                    }
                } catch (err) {
                    logger.error('Failed to fetch retailers', { error: err });
                }
            }, 300);
            return;
        }
        retailerSearchTimer.current = setTimeout(async () => {
            try {
                const response = await qmrsService.fetchRetailers({
                    searchParams: { query, canCreateQmr: true },
                    ignoreCache: true,
                });
                if (response.success && response.data?.retailers) {
                    setRetailerOptions(response.data.retailers.filter((r: any) => r && (r.name || r.code)));
                } else {
                    setRetailerOptions([]);
                }
            } catch (err) {
                logger.error(`Failed to fetch retailers for the query [${query}]`, { error: err, query });
                setRetailerOptions([]);
            }
        }, 300);
    }, []);

    const addVin = useCallback(() => {
        const trimmed = vinInput.trim().toUpperCase();
        if (trimmed && !vins.includes(trimmed)) {
            setVins((prev) => [...prev, trimmed]);
        }
        setVinInput('');
    }, [vinInput, vins]);

    const removeVin = useCallback((vin: string) => {
        setVins((prev) => prev.filter((v) => v !== vin));
    }, []);

    const handleVinKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                addVin();
            }
        },
        [addVin]
    );

    const addDtc = useCallback(() => {
        const trimmed = dtcInput.trim().toUpperCase();
        if (trimmed && !dtcs.includes(trimmed)) {
            setDtcs((prev) => [...prev, trimmed]);
        }
        setDtcInput('');
    }, [dtcInput, dtcs]);

    const removeDtc = useCallback((dtc: string) => {
        setDtcs((prev) => prev.filter((d) => d !== dtc));
    }, []);

    const handleDtcKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                addDtc();
            }
        },
        [addDtc]
    );

    const addCarlineEntry = useCallback(() => {
        if (pendingCarline.length === 0) {
            return;
        }
        setCarlineEntries((prev) => [
            ...prev,
            {
                carline: pendingCarline[0],
                modelYearFrom: pendingMYFrom.length > 0 ? pendingMYFrom[0] : '',
                modelYearTo: pendingMYTo.length > 0 ? pendingMYTo[0] : '',
            },
        ]);
        setPendingCarline([]);
        setPendingMYFrom([]);
        setPendingMYTo([]);
    }, [pendingCarline, pendingMYFrom, pendingMYTo]);

    const removeCarlineEntry = useCallback((index: number) => {
        setCarlineEntries((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleReset = useCallback(() => {
        setVins([]);
        setVinInput('');
        setDtcs([]);
        setDtcInput('');
        setCarlineEntries([]);
        setPendingCarline([]);
        setPendingMYFrom([]);
        setPendingMYTo([]);
        setStartDate('');
        setEndDate('');
        setTechnician([]);
        setSaveId('');
        setRetailerId([]);
        carlineRef.current?.clear();
        myFromRef.current?.clear();
        myToRef.current?.clear();
        technicianRef.current?.clear();
        retailerRef.current?.clear();
        setStatus('');
        setVinGetStatus('');
        setRepairOrderNumber('');
        setOdometerStart('');
        setOdometerEnd('');
        setOdometerUnit('');
        setResults([]);
        setTotalRecords(0);
        setHasSearched(false);
        setCurrentPage(1);
    }, []);

    const buildSearchBody = useCallback(
        (page: number, pageSize: number): TroubleshootingSearchRequest => {
            const body: TroubleshootingSearchRequest = {};
            if (vins.length > 0) {
                body.vins = vins;
            }
            if (dtcs.length > 0) {
                body.dtcs = dtcs;
            }
            if (startDate) {
                body.startDate = formatDateForApi(startDate);
            }
            if (endDate) {
                body.endDate = formatDateForApi(endDate);
            }
            if (repairOrderNumber.trim()) {
                body.repairOrderNumber = repairOrderNumber.trim();
            }
            if (status !== '') {
                body.completion = Number(status);
            }
            if (technician.length > 0) {
                body.technician = technician[0].accountId;
            }
            if (saveId.trim()) {
                body.saveId = saveId.trim();
            }
            if (retailerId.length > 0) {
                body.retailerCode = retailerId[0].code;
            }
            if (odometerStart) {
                body.odometerStart = Number(odometerStart);
            }
            if (odometerEnd) {
                body.odometerEnd = Number(odometerEnd);
            }
            if (vinGetStatus !== '') {
                body.vinGetStatus = Number(vinGetStatus);
            }
            if (odometerUnit) {
                body.odometerUnit = odometerUnit;
            }
            body.page = page;
            body.pageSize = pageSize;

            if (carlineEntries.length > 0) {
                body.carlineModelYearRanges = carlineEntries.map((entry) => ({
                    carline: entry.carline.id,
                    modelYearFrom: entry.modelYearFrom ? Number(entry.modelYearFrom) : null,
                    modelYearTo: entry.modelYearTo ? Number(entry.modelYearTo) : null,
                }));
            }

            return body;
        },
        [
            vins,
            dtcs,
            startDate,
            endDate,
            repairOrderNumber,
            status,
            technician,
            saveId,
            retailerId,
            odometerStart,
            odometerEnd,
            vinGetStatus,
            odometerUnit,
            carlineEntries,
        ]
    );

    const handleSearch = useCallback(
        async (page = 1) => {
            setLoading(true);
            setHasSearched(true);
            setCurrentPage(page);

            const body = buildSearchBody(page, ROWS_PER_PAGE);

            try {
                const res = await docsService.searchTroubleshootingSessions({ body });
                if (res.success && res.data?.result?.data) {
                    setResults(res.data.result.data);
                    setTotalRecords(res.data.result.totalCount ?? res.data.result.data.length);
                } else {
                    setResults([]);
                    setTotalRecords(0);
                }
            } catch (err) {
                logger.error('Failed to search troubleshooting sessions', { error: err });
                setResults([]);
                setTotalRecords(0);
            } finally {
                setLoading(false);
            }
        },
        [buildSearchBody]
    );

    handleSearchRef.current = handleSearch;

    const handleViewDocument = useCallback(
        async (result: TroubleshootingSearchResult) => {
            await openDocumentTab(
                async () => {
                    const documentUrl = await fetchCdnDocumentUrlByDocId(result.documentId, token);
                    if (!documentUrl) {
                        return null;
                    }
                    const basePath = documentUrl.substring(0, documentUrl.lastIndexOf('/') + 1);
                    const contentPath = result.contentPath.startsWith('/')
                        ? result.contentPath.slice(1)
                        : result.contentPath;
                    return `${basePath}${contentPath}?saveId=${result.troubleShootingId}`;
                },
                () => setPopupBlocked(true)
            );
        },
        [token]
    );

    const handleExportReport = useCallback(async () => {
        if (totalRecords === 0) {
            return;
        }
        setExporting(true);
        try {
            const body = buildSearchBody(1, totalRecords);
            const res = await docsService.searchTroubleshootingSessions({ body });
            const exportResults = res.success ? (res.data?.result?.data ?? []) : [];
            if (exportResults.length === 0) {
                return;
            }

            const headers = [
                'SaveId',
                'RO#',
                'SD Year',
                'SD Model',
                'DTC',
                'VIN',
                'VIN Year',
                'VIN Carline',
                'Status',
                'Username',
                'Full Name',
                'Retailer Code',
                'Update Date',
                'Odometer',
                'Unit',
                'Input Method',
            ];
            const rows = exportResults.map((r) => [
                r.searchId,
                r.repairOrderNumber,
                r.modelYear,
                r.carlineName,
                r.dtc,
                r.vin,
                r.vinModelYear,
                r.vinCarlineName,
                statusLabel(r.completion),
                r.userName,
                r.fullName,
                r.dealerNumber,
                formatDate(r.updatedDate),
                r.odometer,
                r.odometerUnit,
                vinInputLabel(r.vinGetStatus),
            ]);
            const csvContent = [headers, ...rows].map((row) => row.map((v) => `"${v}"`).join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'troubleshooting-report.csv';
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            logger.error('Failed to export the troubleshooting report', { error: err });
        } finally {
            setExporting(false);
        }
    }, [totalRecords, buildSearchBody]);

    const totalPages = Math.ceil(totalRecords / ROWS_PER_PAGE);
    const startEntry = totalRecords > 0 ? (currentPage - 1) * ROWS_PER_PAGE + 1 : 0;
    const endEntry = Math.min(currentPage * ROWS_PER_PAGE, totalRecords);

    const resultsRef = useRef<HTMLDivElement>(null);
    const [highlightResults, setHighlightResults] = useState(false);

    useEffect(() => {
        if (!loading && hasSearched && results.length > 0 && resultsRef.current) {
            resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlightResults(true);
            const timer = setTimeout(() => setHighlightResults(false), 1500);
            return () => clearTimeout(timer);
        }
    }, [loading, hasSearched, results]);

    const maxVisiblePages = 5;
    const pageNumbers = useMemo(() => {
        if (totalPages <= maxVisiblePages) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const end = Math.min(totalPages, start + maxVisiblePages - 1);
        if (end - start + 1 < maxVisiblePages) {
            start = Math.max(1, end - maxVisiblePages + 1);
        }
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }, [totalPages, currentPage]);

    return (
        <div className={classes.page}>
            <AdminHubHeader breadcrumbs={breadcrumbs} />
            <div className={classes.container}>
                <PopupGuidanceBanner
                    blocked={popupBlocked}
                    onClose={handleGuidanceClose}
                    show={showGuidance}
                    style={{ marginBottom: 24 }}
                />
                {!showGuidance && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                        <PopupGuidanceHelpPill onPress={() => setGuidanceReopened(true)} />
                    </div>
                )}
                {/* ---- Collapsible search panel ---- */}
                <div className={classes.searchPanel}>
                    <div className={classes.searchPanelHeader} onClick={() => setSearchOpen(!searchOpen)}>
                        <span className={classes.searchPanelHeaderTitle}>TROUBLESHOOTING SEARCH CRITERIA</span>
                        <Icon color="white" name={searchOpen ? 'minus' : 'plus'} size={16} status={true} />
                    </div>

                    {searchOpen && (
                        <div className={classes.searchPanelBody}>
                            <div className={classes.instruction}>Enter at least one search criteria.</div>

                            <Row>
                                <Col md={6}>
                                    <div className={classes.fieldGroup}>
                                        <div className={classes.fieldLabelRow}>
                                            <span className={classes.fieldLabel} style={{ marginBottom: 0 }}>
                                                VIN
                                            </span>
                                            <Button
                                                onPress={addVin}
                                                size="small"
                                                title="+ Add VIN"
                                                titleStyle={{ fontSize: 12 }}
                                                variant="primary"
                                            />
                                        </div>
                                        <Form.Control
                                            className={classes.fieldInput}
                                            onChange={(e) => setVinInput(e.target.value)}
                                            onKeyDown={handleVinKeyDown}
                                            placeholder="Last 8 or Full 17 or * for partial"
                                            type="text"
                                            value={vinInput}
                                        />
                                        {vins.length > 0 && (
                                            <div className={classes.chipList}>
                                                {vins.map((vin) => (
                                                    <span className={classes.chip} key={vin}>
                                                        {vin}
                                                        <span
                                                            className={classes.chipRemove}
                                                            onClick={() => removeVin(vin)}
                                                        >
                                                            &times;
                                                        </span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className={classes.fieldGroup}>
                                        <div className={classes.fieldLabelRow}>
                                            <span className={classes.fieldLabel} style={{ marginBottom: 0 }}>
                                                DTC
                                            </span>
                                            <Button
                                                onPress={addDtc}
                                                size="small"
                                                title="+ Add DTC"
                                                titleStyle={{ fontSize: 12 }}
                                                variant="primary"
                                            />
                                        </div>
                                        <Form.Control
                                            className={classes.fieldInput}
                                            onChange={(e) => setDtcInput(e.target.value)}
                                            onKeyDown={handleDtcKeyDown}
                                            placeholder="Use * for partial search"
                                            type="text"
                                            value={dtcInput}
                                        />
                                        {dtcs.length > 0 && (
                                            <div className={classes.chipList}>
                                                {dtcs.map((d) => (
                                                    <span className={classes.chip} key={d}>
                                                        {d}
                                                        <span
                                                            className={classes.chipRemove}
                                                            onClick={() => removeDtc(d)}
                                                        >
                                                            &times;
                                                        </span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={4}>
                                    <div className={`${classes.fieldGroup} ${classes.typeaheadWrap}`}>
                                        <span className={classes.fieldLabel}>Carline</span>
                                        <Typeahead
                                            id="ts-carline"
                                            labelKey="description"
                                            onChange={(s) => setPendingCarline(s as FilterCarline[])}
                                            options={carlineOptions}
                                            placeholder="Choose Carline"
                                            ref={carlineRef}
                                            selected={pendingCarline}
                                            size="sm"
                                        >
                                            <div className={classes.clearIcon}>
                                                {pendingCarline.length > 0 ? (
                                                    <Icon
                                                        name="x-close"
                                                        onPress={() => {
                                                            setPendingCarline([]);
                                                            carlineRef.current?.clear();
                                                        }}
                                                    />
                                                ) : (
                                                    <Icon
                                                        name="chevron-down"
                                                        onPress={() => {
                                                            carlineRef.current?.toggleMenu();
                                                            carlineRef.current?.inputNode?.focus?.();
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </Typeahead>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className={`${classes.fieldGroup} ${classes.typeaheadWrap}`}>
                                        <span className={classes.fieldLabel}>Model Year</span>
                                        <Row>
                                            <Col xs={6}>
                                                <Typeahead
                                                    id="ts-my-from"
                                                    onChange={(s) => setPendingMYFrom(s as string[])}
                                                    options={modelYearOptions}
                                                    placeholder="From"
                                                    ref={myFromRef}
                                                    selected={pendingMYFrom}
                                                    size="sm"
                                                >
                                                    <div className={classes.clearIcon}>
                                                        {pendingMYFrom.length > 0 ? (
                                                            <Icon
                                                                name="x-close"
                                                                onPress={() => {
                                                                    setPendingMYFrom([]);
                                                                    myFromRef.current?.clear();
                                                                }}
                                                            />
                                                        ) : (
                                                            <Icon
                                                                name="chevron-down"
                                                                onPress={() => {
                                                                    myFromRef.current?.toggleMenu();
                                                                    myFromRef.current?.inputNode?.focus?.();
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </Typeahead>
                                            </Col>
                                            <Col xs={6}>
                                                <Typeahead
                                                    id="ts-my-to"
                                                    onChange={(s) => setPendingMYTo(s as string[])}
                                                    options={modelYearOptions}
                                                    placeholder="To"
                                                    ref={myToRef}
                                                    selected={pendingMYTo}
                                                    size="sm"
                                                >
                                                    <div className={classes.clearIcon}>
                                                        {pendingMYTo.length > 0 ? (
                                                            <Icon
                                                                name="x-close"
                                                                onPress={() => {
                                                                    setPendingMYTo([]);
                                                                    myToRef.current?.clear();
                                                                }}
                                                            />
                                                        ) : (
                                                            <Icon
                                                                name="chevron-down"
                                                                onPress={() => {
                                                                    myToRef.current?.toggleMenu();
                                                                    myToRef.current?.inputNode?.focus?.();
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </Typeahead>
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                                <Col md={4} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 20 }}>
                                    <div className={classes.addButton}>
                                        <Button
                                            onPress={addCarlineEntry}
                                            size="small"
                                            title="+ Add Carline/Model Year"
                                            titleStyle={{ fontSize: 12 }}
                                            variant="primary"
                                        />
                                    </div>
                                </Col>
                            </Row>
                            {carlineEntries.length > 0 && (
                                <div className={classes.chipList} style={{ marginTop: 0, marginBottom: 16 }}>
                                    {carlineEntries.map((entry, i) => (
                                        <span className={classes.carlineTag} key={i}>
                                            {entry.carline.description}
                                            {entry.modelYearFrom && ` (${entry.modelYearFrom}`}
                                            {entry.modelYearTo
                                                ? ` - ${entry.modelYearTo})`
                                                : entry.modelYearFrom
                                                  ? ')'
                                                  : ''}
                                            <span className={classes.chipRemove} onClick={() => removeCarlineEntry(i)}>
                                                &times;
                                            </span>
                                        </span>
                                    ))}
                                </div>
                            )}

                            <Row>
                                <Col md={4}>
                                    <div className={classes.fieldGroup}>
                                        <span className={classes.fieldLabel}>Create Date From</span>
                                        <Form.Control
                                            className={classes.fieldInput}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            type="date"
                                            value={startDate}
                                        />
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className={classes.fieldGroup}>
                                        <span className={classes.fieldLabel}>Create Date To</span>
                                        <Form.Control
                                            className={classes.fieldInput}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            type="date"
                                            value={endDate}
                                        />
                                    </div>
                                </Col>
                                <Col md={4} />
                            </Row>

                            <Row>
                                <Col md={4}>
                                    <div className={`${classes.fieldGroup} ${classes.typeaheadWrap}`}>
                                        <span className={classes.fieldLabel}>Technician</span>
                                        <Typeahead
                                            filterBy={canSearchAll ? () => true : undefined}
                                            id="ts-technician"
                                            labelKey={(option: any) => option.soaUsername || ''}
                                            onChange={(s) =>
                                                setTechnician(
                                                    s as {
                                                        accountId: string;
                                                        soaUsername: string;
                                                        firstName: string;
                                                        lastName: string;
                                                    }[]
                                                )
                                            }
                                            onInputChange={canSearchAll ? handleTechnicianSearch : undefined}
                                            options={technicianOptions}
                                            placeholder="Search SOA Username"
                                            ref={technicianRef}
                                            selected={technician}
                                            size="sm"
                                        >
                                            <div className={classes.clearIcon}>
                                                {technician.length > 0 ? (
                                                    <Icon
                                                        name="x-close"
                                                        onPress={() => {
                                                            setTechnician([]);
                                                            technicianRef.current?.clear();
                                                        }}
                                                    />
                                                ) : (
                                                    <Icon
                                                        name="chevron-down"
                                                        onPress={() => {
                                                            technicianRef.current?.toggleMenu();
                                                            technicianRef.current?.inputNode?.focus?.();
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </Typeahead>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className={classes.fieldGroup}>
                                        <span className={classes.fieldLabel}>Save ID</span>
                                        <Form.Control
                                            className={classes.fieldInput}
                                            onChange={(e) => setSaveId(e.target.value)}
                                            placeholder="Enter Save ID"
                                            type="text"
                                            value={saveId}
                                        />
                                    </div>
                                </Col>
                                <Col md={4} />
                            </Row>

                            {canSearchAll && (
                                <Row>
                                    <Col md={4}>
                                        <div className={`${classes.fieldGroup} ${classes.typeaheadWrap}`}>
                                            <span className={classes.fieldLabel}>Retailer</span>
                                            <Typeahead
                                                id="ts-retailer"
                                                labelKey={(option: any) =>
                                                    `${option.name || ''} (${option.code || ''})`.trim()
                                                }
                                                onChange={(s) => setRetailerId(s as Retailer[])}
                                                onInputChange={handleRetailerSearch}
                                                options={retailerOptions}
                                                placeholder="Search Retailer"
                                                ref={retailerRef}
                                                selected={retailerId}
                                                size="sm"
                                            >
                                                <div className={classes.clearIcon}>
                                                    {retailerId.length > 0 ? (
                                                        <Icon
                                                            name="x-close"
                                                            onPress={() => {
                                                                setRetailerId([]);
                                                                retailerRef.current?.clear();
                                                            }}
                                                        />
                                                    ) : (
                                                        <Icon
                                                            name="chevron-down"
                                                            onPress={() => {
                                                                retailerRef.current?.toggleMenu();
                                                                retailerRef.current?.inputNode?.focus?.();
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </Typeahead>
                                        </div>
                                    </Col>
                                    <Col md={8} />
                                </Row>
                            )}

                            <Row>
                                <Col md={4}>
                                    <div className={classes.fieldGroup}>
                                        <span className={classes.fieldLabel}>Status</span>
                                        <Form.Control
                                            as="select"
                                            className={classes.fieldInput}
                                            onChange={(e: any) => setStatus(e.target.value)}
                                            value={status}
                                        >
                                            <option value="">Choose Status</option>
                                            <option value="0">In Progress</option>
                                            <option value="1">Completed</option>
                                        </Form.Control>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className={classes.fieldGroup}>
                                        <span className={classes.fieldLabel}>VIN Input</span>
                                        <Form.Control
                                            as="select"
                                            className={classes.fieldInput}
                                            onChange={(e: any) => setVinGetStatus(e.target.value)}
                                            value={vinGetStatus}
                                        >
                                            <option value="">Choose Input Method</option>
                                            <option value="0">Manual</option>
                                            <option value="1">Auto</option>
                                        </Form.Control>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className={classes.fieldGroup}>
                                        <span className={classes.fieldLabel}>RO Number</span>
                                        <Form.Control
                                            className={classes.fieldInput}
                                            maxLength={20}
                                            onChange={(e) => setRepairOrderNumber(e.target.value)}
                                            placeholder="Alpha-Numeric of up to 20"
                                            type="text"
                                            value={repairOrderNumber}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={4}>
                                    <div className={classes.fieldGroup}>
                                        <span className={classes.fieldLabel}>Odometer From</span>
                                        <Form.Control
                                            className={classes.fieldInput}
                                            onChange={(e) => setOdometerStart(e.target.value)}
                                            placeholder="Leave blank or enter greater than 0"
                                            type="number"
                                            value={odometerStart}
                                        />
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className={classes.fieldGroup}>
                                        <span className={classes.fieldLabel}>Odometer To</span>
                                        <Form.Control
                                            className={classes.fieldInput}
                                            onChange={(e) => setOdometerEnd(e.target.value)}
                                            placeholder="Leave blank or enter greater than 0"
                                            type="number"
                                            value={odometerEnd}
                                        />
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className={classes.fieldGroup}>
                                        <span className={classes.fieldLabel}>&nbsp;</span>
                                        <div className={classes.radioGroup}>
                                            <label className={classes.radioLabel}>
                                                <input
                                                    checked={odometerUnit === 'mile'}
                                                    name="odometerUnit"
                                                    onChange={(e) => setOdometerUnit(e.target.value)}
                                                    type="radio"
                                                    value="mile"
                                                />
                                                Miles
                                            </label>
                                            <label className={classes.radioLabel}>
                                                <input
                                                    checked={odometerUnit === 'kilometer'}
                                                    name="odometerUnit"
                                                    onChange={(e) => setOdometerUnit(e.target.value)}
                                                    type="radio"
                                                    value="kilometer"
                                                />
                                                KM
                                            </label>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <div className={classes.actionBar}>
                                <Button
                                    disabled={!hasCriteria}
                                    onPress={() => handleSearch()}
                                    title="Search"
                                    variant="primary"
                                />
                                <Button
                                    disabled={!hasCriteria}
                                    onPress={handleReset}
                                    title="Clear"
                                    variant="secondary"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {loading && (
                    <div className={classes.emptyState}>
                        <Typography variant="caption2">Searching...</Typography>
                    </div>
                )}

                {hasSearched && !loading && results.length === 0 && (
                    <div className={classes.emptyState}>
                        <Icon color="grayFive" name="magnifying-glass" size={28} />
                        <Typography style={{ color: colors.grayFive }} variant="caption2">
                            No troubleshooting sessions found. Try adjusting your filters.
                        </Typography>
                    </div>
                )}

                <div
                    className={`${classes.resultsSection} ${!loading && results.length > 0 ? classes.resultsSectionVisible : classes.resultsSectionHidden} ${highlightResults ? classes.resultsSectionHighlight : ''}`}
                    ref={resultsRef}
                >
                    {!loading && results.length > 0 && (
                        <>
                            <div className={classes.resultsHeader}>
                                <span className={classes.resultCount}>
                                    {totalRecords} result{totalRecords !== 1 ? 's' : ''} found
                                </span>
                                <Button
                                    disabled={exporting}
                                    onPress={handleExportReport}
                                    title={exporting ? 'Exporting…' : 'Export CSV'}
                                    variant="ghost-blue"
                                />
                            </div>

                            <div className={classes.tableWrap}>
                                <table className={classes.table}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '6%' }}>Save ID</th>
                                            <th style={{ width: '5%' }}>RO #</th>
                                            <th style={{ width: '4%' }}>SD Year</th>
                                            <th style={{ width: '5%' }}>SD Model</th>
                                            <th style={{ width: '5%' }}>DTC</th>
                                            <th style={{ width: '10%' }}>VIN</th>
                                            <th style={{ width: '5%' }}>VIN Year</th>
                                            <th style={{ width: '7%' }}>VIN Carline</th>
                                            <th style={{ width: '9%' }}>Status</th>
                                            <th style={{ width: '7%' }}>Username</th>
                                            <th style={{ width: '7%' }}>Full Name</th>
                                            <th style={{ width: '7%' }}>Created</th>
                                            <th style={{ width: '6%' }}>Updated</th>
                                            <th style={{ width: '5%' }}>Odometer</th>
                                            <th style={{ width: '3%' }}>Unit</th>
                                            <th style={{ width: '5%' }}>Input</th>
                                            <th style={{ width: '4%' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.map((r, idx) => (
                                            <tr key={r.saveId + idx}>
                                                <td
                                                    style={{
                                                        whiteSpace: 'nowrap',
                                                        fontSize: 12,
                                                        fontFamily: 'monospace',
                                                    }}
                                                    title={r.searchId}
                                                >
                                                    {r.searchId}
                                                </td>
                                                <td className={classes.cellTruncate} title={r.repairOrderNumber}>
                                                    {r.repairOrderNumber}
                                                </td>
                                                <td>{r.modelYear}</td>
                                                <td className={classes.cellTruncate} title={r.carlineName}>
                                                    {r.carlineName}
                                                </td>
                                                <td className={classes.cellTruncate} title={r.dtc}>
                                                    {r.dtc}
                                                </td>
                                                <td
                                                    className={classes.cellTruncate}
                                                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                                                    title={r.vin}
                                                >
                                                    {r.vin}
                                                </td>
                                                <td>{r.vinModelYear}</td>
                                                <td className={classes.cellTruncate} title={r.vinCarlineName}>
                                                    {r.vinCarlineName}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`${classes.statusBadge} ${
                                                            r.completion === 1
                                                                ? classes.statusCompleted
                                                                : classes.statusInProgress
                                                        }`}
                                                    >
                                                        {statusLabel(r.completion)}
                                                    </span>
                                                </td>
                                                <td className={classes.cellTruncate} title={r.userName}>
                                                    {r.userName}
                                                </td>
                                                <td className={classes.cellTruncate} title={r.fullName}>
                                                    {r.fullName}
                                                </td>
                                                <td className={classes.cellTruncate} title={formatDate(r.createdDate)}>
                                                    {formatDate(r.createdDate)}
                                                </td>
                                                <td className={classes.cellTruncate} title={formatDate(r.updatedDate)}>
                                                    {formatDate(r.updatedDate)}
                                                </td>
                                                <td>{r.odometer}</td>
                                                <td>{r.odometerUnit}</td>
                                                <td>{vinInputLabel(r.vinGetStatus)}</td>
                                                <td>
                                                    <span
                                                        className={classes.viewLink}
                                                        onClick={() => handleViewDocument(r)}
                                                    >
                                                        view
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className={classes.paginationBar}>
                                <span className={classes.pageInfo}>
                                    Showing {startEntry}&ndash;{endEntry} of {totalRecords}
                                </span>
                                <div className={classes.pageControls}>
                                    <button
                                        className={currentPage === 1 ? classes.pageBtnDisabled : classes.pageBtn}
                                        disabled={currentPage === 1}
                                        onClick={() => currentPage > 1 && handleSearch(currentPage - 1)}
                                    >
                                        Prev
                                    </button>
                                    {pageNumbers[0] > 1 && (
                                        <>
                                            <button className={classes.pageBtn} onClick={() => handleSearch(1)}>
                                                1
                                            </button>
                                            {pageNumbers[0] > 2 && <span style={{ padding: '0 4px' }}>&hellip;</span>}
                                        </>
                                    )}
                                    {pageNumbers.map((p) => (
                                        <button
                                            className={p === currentPage ? classes.pageBtnActive : classes.pageBtn}
                                            key={p}
                                            onClick={() => handleSearch(p)}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                    {pageNumbers[pageNumbers.length - 1] < totalPages && (
                                        <>
                                            {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                                                <span style={{ padding: '0 4px' }}>&hellip;</span>
                                            )}
                                            <button
                                                className={classes.pageBtn}
                                                onClick={() => handleSearch(totalPages)}
                                            >
                                                {totalPages}
                                            </button>
                                        </>
                                    )}
                                    <button
                                        className={
                                            currentPage === totalPages ? classes.pageBtnDisabled : classes.pageBtn
                                        }
                                        disabled={currentPage === totalPages}
                                        onClick={() => currentPage < totalPages && handleSearch(currentPage + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
